import { Control, Controller, FieldErrors } from "react-hook-form";

import {
  Checkbox,
  Grid,
  Input,
  Paragraph,
  Separator,
  getGridLayout,
} from "@/components";
import { CarrierSearchField } from "@/forms/CarrierSearchField";
import type { InsuranceSchemaType } from "../tabs/insurance";

export interface InsuranceInformationProps {
  control: Control<InsuranceSchemaType>;
  errors: FieldErrors<InsuranceSchemaType>;
  isLocked?: boolean;
}

export function InsuranceInformation({
  control,
  errors,
  isLocked = false,
}: InsuranceInformationProps) {
  return (
    <Grid>
      {isLocked && (
        <Paragraph color="$textSecondary" size="$s" {...getGridLayout(12)}>
          Your insurance is locked while your Medicare number is on file with
          your program enrollment. Contact support to update it.
        </Paragraph>
      )}
      {!isLocked && (
        <>
          <Controller
            control={control}
            name="carrier"
            render={({ field: { value, onChange, onBlur }, fieldState }) => (
              <CarrierSearchField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                errorText={fieldState.error?.message}
                rootProps={{ ...getGridLayout(6) }}
              />
            )}
          />
          <Controller
            control={control}
            name="planName"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input.Controlled
                label="Plan name"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                errorText={errors.planName?.message}
                rootProps={{ ...getGridLayout(6) }}
              />
            )}
          />
          <Controller
            control={control}
            name="memberId"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input.Controlled
                label="Member ID"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                errorText={errors.memberId?.message}
                rootProps={{ ...getGridLayout(6) }}
              />
            )}
          />
          <Controller
            control={control}
            name="groupNumber"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input.Controlled
                label="Group number"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                errorText={errors.groupNumber?.message}
                rootProps={{ ...getGridLayout(6) }}
              />
            )}
          />
          <Controller
            control={control}
            name="rxBin"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input.Controlled
                label="RX BIN"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                errorText={errors.rxBin?.message}
                rootProps={{ ...getGridLayout(6) }}
              />
            )}
          />
          <Controller
            control={control}
            name="rxPcn"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input.Controlled
                label="RX PCN"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                errorText={errors.rxPcn?.message}
                rootProps={{ ...getGridLayout(6) }}
              />
            )}
          />
          <Controller
            control={control}
            name="rxGroup"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input.Controlled
                label="RX group"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                errorText={errors.rxGroup?.message}
                rootProps={{ ...getGridLayout(6) }}
              />
            )}
          />
          <Separator
            borderColor="$fillNeutral"
            marginVertical="$100"
            {...getGridLayout(12)}
          />
        </>
      )}
      <Controller
        control={control}
        name="medicareBeneficiaryId"
        render={({ field: { onChange, onBlur, value } }) => (
          <Input.Controlled
            label="Medicare number (MBI)"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            readonly={isLocked}
            errorText={errors.medicareBeneficiaryId?.message}
            rootProps={{ ...getGridLayout(6) }}
          />
        )}
      />
      <Controller
        control={control}
        name="userIsPolicyHolder"
        render={({ field: { onChange, value } }) => (
          <Checkbox
            label="I am the insurance policy holder"
            labelProps={{ fontSize: "$sm", fontWeight: "normal" }}
            checked={value}
            onCheckedChange={(checked) => onChange(checked === true)}
            disabled={isLocked}
            errorText={errors.userIsPolicyHolder?.message}
            rootProps={{ ...getGridLayout(12) }}
          />
        )}
      />
    </Grid>
  );
}
